import { useState, useRef, useEffect } from "react";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { useNavigate } from "react-router-dom";
import api from "../../utils/apiClient";
import APIConstant from "../../utils/pathConstants";
import Toast from "../Shared/Toast/Toast";
import Loader from "../Shared/Loader/Loader";
import CommonConfig from "../../utils/constant";
import logo from "../../assets/images/dune-main-logo.svg";
import portlinkLogo from "../../assets/images/portlinkLogo.svg";
import { Dialog } from "primereact/dialog";
import AccountStore from "../../utils/accountStore";
import useRecaptchaV2 from "../../utils/useRecaptchaV2";

const OTPLogin = () => {
    const [step, setStep] = useState("send");
    const [accounts, setAccounts] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [state, setState] = useState({
        phone: "",
        otp: "",
        phoneErr: false,
        phoneHelperText: "",
        otpErr: false,
        otpHelperText: "",
        openLoginConfirm: false,
        openSchoolSelect: false,
        openSessionLimit: false,
    });
    const [sessionList, setSessionList] = useState([]);
    const [pendingVerify, setPendingVerify] = useState(null);

    const navigate = useNavigate();
    const captcha = useRecaptchaV2();
    const pendingCaptchaRef = useRef(null);

    useEffect(() => {
        if (captcha.visible) captcha.render();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [captcha.visible]);

    const handleCheck = (e, type) => {
        const value = e.target.value;
        if (type === "otp" && /^\d{6}$/.test(value) && !state.otpErr) {
            setState((s) => ({ ...s, otp: value, otpErr: false, otpHelperText: "" }));
            setTimeout(() => handleVerifyOTP({ preventDefault: () => { } }, value), 50);
            return;
        }
        if (type === "phone") {
            if (!value || value.trim() === "") {
                setState((s) => ({
                    ...s,
                    phone: value,
                    phoneErr: true,
                    phoneHelperText: "Phone number is required",
                }));
            } else if (!/^\d{10}$/.test(value)) {
                setState((s) => ({
                    ...s,
                    phone: value,
                    phoneErr: true,
                    phoneHelperText: "Enter valid 10-digit phone number",
                }));
            } else {
                setState((s) => ({
                    ...s,
                    phone: value,
                    phoneErr: false,
                    phoneHelperText: "",
                }));
            }
        }
        if (type === "otp") {
            if (!value || value.trim() === "") {
                setState((s) => ({
                    ...s,
                    otp: value,
                    otpErr: true,
                    otpHelperText: "OTP is required",
                }));
            } else if (!/^\d{4,6}$/.test(value)) {
                setState((s) => ({
                    ...s,
                    otp: value,
                    otpErr: true,
                    otpHelperText: "Enter valid OTP",
                }));
            } else {
                setState((s) => ({
                    ...s,
                    otp: value,
                    otpErr: false,
                    otpHelperText: "",
                }));
            }
        }
    };

    const getRecaptchaToken = (actionType) => {
        return new Promise((resolve, reject) => {
            if (!window.grecaptcha) {
                return reject(new Error('reCAPTCHA not loaded'));
            }

            window.grecaptcha.ready(() => {
                window.grecaptcha.execute(process.env.REACT_APP_GOOGLE_SITE_KEY, { action: actionType })
                    .then(token => {
                        if (!token) {
                            reject(new Error('Failed to get reCAPTCHA token'));
                        } else {
                            resolve(token);
                        }
                    })
                    .catch(err => reject(err));
            });
        });
    };

    const handleSendOTP = async (e) => {
        e?.preventDefault?.();
        if (state.phoneErr || !state.phone) {
            Toast.error({ message: "Enter a valid phone number" });
            return;
        }

        Loader.show();
        try {
            const v2Token = captcha.tokenRef.current;
            let token = null;
            try {
                token = await getRecaptchaToken('sendOTP');
            } catch (e) {
                if (!v2Token) throw e;
            }
            const payload = {
                Phone: state.phone,
                DeviceType: "Web",
                RecaptchaToken: token
            };
            if (v2Token) {
                payload.RecaptchaV2Token = v2Token;
                captcha.tokenRef.current = null;
            }
            const res = await api.post(APIConstant.path.sendOTP, payload);
            captcha.hide();

            if (res?.success) {
                Toast.success({ message: res.message || "OTP sent successfully" });
                setStep("verify");
            } else {
                Toast.error({ message: res.message || "Failed to send OTP" });
            }
        } catch (err) {
            if (err?.captchaFallback) {
                captcha.show(() => handleSendOTP());
                Toast.error({ message: 'Please confirm you are not a robot to continue.' });
            } else {
                Toast.error({ message: err.message || "Error while sending OTP" });
            }
        } finally {
            Loader.hide();
        }
    };

    // Multi-school: a school was picked after OTP entry -> verify + login into it.
    const handleSelectSchool = (acc) => {
        setSelectedUserId(acc.userId);
        setState((s) => ({ ...s, openSchoolSelect: false }));
        proceedVerify(false, state.otp, { userId: acc.userId, accounts });
    };

    const formatLastActive = (d) => {
        if (!d) return '';
        const diff = Date.now() - new Date(d).getTime();
        const m = Math.floor(diff / 60000);
        if (m < 1) return 'active just now';
        if (m < 60) return `active ${m} min ago`;
        const h = Math.floor(m / 60);
        if (h < 24) return `active ${h} hr ago`;
        const days = Math.floor(h / 24);
        return `active ${days} day${days > 1 ? 's' : ''} ago`;
    };

    // At the max-session cap, pick a device to log out then retry the OTP verify.
    const handleRemoveSession = async (sessionId) => {
        const pending = pendingVerify;
        try {
            Loader.show();
            const res = await api.post(APIConstant.path.deleteSessionById, { sessionId });
            Loader.hide();
            if (!res.success) {
                Toast.error({ message: res.message || 'Failed to log out that device' });
                return;
            }
            setState((s) => ({ ...s, openSessionLimit: false }));
            setPendingVerify(null);
            await proceedVerify(pending?.isAlready ?? false, pending?.otpOverride ?? state.otp, pending?.opts ?? {});
        } catch (err) {
            Loader.hide();
            Toast.error({ message: err.message || 'Failed to log out that device' });
        }
    };

    const callLoginUser = async (isAlready, otpOverride, opts = {}) => {
        const v2Token = captcha.tokenRef.current;
        let token = null;
        try {
            token = await getRecaptchaToken('verifyOTP');
        } catch (e) {
            if (!v2Token) throw e;
        }
        const uid = opts.userId !== undefined ? opts.userId : selectedUserId;
        const accs = opts.accounts !== undefined ? opts.accounts : accounts;

        const loginData = {
            Phone: state.phone,
            OTP: otpOverride || state.otp,
            DeviceType: 'Web',
            DeviceId: CommonConfig.getDeviceId(),
            isAlreadyLogin: isAlready,
            IsOTPLogin: true,
            UserType: 'User',
            RecaptchaToken: token,
        };
        if (uid) {
            loginData.UserId = uid;
        }
        if (v2Token) {
            loginData.RecaptchaV2Token = v2Token;
            captcha.tokenRef.current = null;
        }

        const res = await api.post(APIConstant.path.verifyOTPLogin, loginData);
        captcha.hide();

        if (res?.redirectToHome) {
            Toast.error({ message: res.message });
            setTimeout(() => navigate('/'), 1500);
            return;
        }

        if (res?.sessionLimitReached) {
            setSessionList(res.sessions || []);
            setPendingVerify({ isAlready, otpOverride, opts });
            setState((s) => ({ ...s, openSessionLimit: true }));
            return;
        }

        if (!res?.success) {
            setState((s) => ({ ...s, otp: "", otpErr: false, otpHelperText: "" }));
            Toast.error({ message: res?.message || 'Invalid OTP' });
            return;
        }

        // OTP verified -> multi-school identity: open the picker (OTP is kept for the follow-up call).
        if (res?.multi && Array.isArray(res.accounts) && res.accounts.length > 1) {
            setAccounts(res.accounts);
            setSelectedUserId(null);
            setState((s) => ({ ...s, openSchoolSelect: true }));
            return;
        }

        if (res.success && res?.data && !CommonConfig.isEmpty(res?.data?.Token)) {
            const userData = res.data;
            localStorage.setItem('ERPLoginData', JSON.stringify(userData));
            CommonConfig.setCookie('selectedAcademicYear', userData?.SelectedAcademicYear ?? '');
            CommonConfig.setCookie('access_token', userData?.Token?.toString());
            CommonConfig.setCookie('refresh_token', userData?.RefreshToken?.toString());
            CommonConfig.setCookie('refreshValue', userData?.RefreshValue?.toString());
            CommonConfig.setCookie('loggedIn', true.toString());

            AccountStore.saveActiveAccount(userData);
            if (accs?.length) {
                AccountStore.setDiscoveredSchools(accs);
            }

            setTimeout(async () => {
                if (userData?.UserRole === 'Parent') {
                    await CommonConfig.getUserMenu(userData, navigate, 'OnClick');
                } else {
                    await CommonConfig.getUserMenu(userData, navigate, 'Login');
                }
            }, 2500);

            handleClose();
            Toast.success({ message: res.message });
        } else {
            Toast.error({ message: res.message });
        }
    };

    // Session-conflict dance + verify. Catches the 409 to raise the dialog.
    const proceedVerify = async (isAlready, otpOverride, opts = {}) => {
        Loader.show();
        try {
            if (isAlready) {
                const sessionRes = await deleteUserSession(state?.phone);
                if (sessionRes?.success) {
                    Toast.success({ message: sessionRes?.message ?? 'Previous session logged out successfully' });
                } else {
                    Toast.error({ message: sessionRes?.message || 'Failed to logout previous session' });
                }
                await new Promise((resolve) => setTimeout(resolve, 2000));
            }
            await callLoginUser(isAlready, otpOverride, opts);
        } catch (err) {
            if (err?.captchaFallback) {
                pendingCaptchaRef.current = { isAlready, otpOverride, opts };
                captcha.show(() => proceedVerify(
                    pendingCaptchaRef.current?.isAlready ?? false,
                    pendingCaptchaRef.current?.otpOverride ?? state.otp,
                    pendingCaptchaRef.current?.opts ?? {},
                ));
                Toast.error({ message: 'Please confirm you are not a robot to continue.' });
            } else {
                setState((s) => ({ ...s, otp: "" }));
                if (err?.statusCode === 409) {
                    setState((prevState) => ({ ...prevState, openLoginConfirm: true }));
                } else {
                    Toast.error({ message: err.message || 'Login failed' });
                }
            }
        } finally {
            Loader.hide();
        }
    };

    // OTP entered -> verify the OTP first; the school list (if multi) is revealed by the server only after that.
    const handleVerifyOTP = async (e, otpOverride) => {
        e?.preventDefault?.();
        const otpToUse = otpOverride || state.otp;
        if (!otpToUse || !/^\d{4,6}$/.test(otpToUse)) {
            Toast.error({ message: "Enter valid OTP" });
            return;
        }
        await proceedVerify(false, otpToUse, { userId: null, accounts: [] });
    };

    const handleClose = () => {
        setState((prevState) => ({
            ...prevState,
            openLoginConfirm: false,
        }));
    };

    const deleteUserSession = async (userId) => {
        try {
            const data = { username: userId };
            if (selectedUserId) {
                data.UserId = selectedUserId;
            }
            const res = await api.post(APIConstant.path.deleteSession, data);
            return res;
        } catch (err) {
            console.error('Error deleting session:', err);
            return { success: false, message: err.message };
        }
    };

    const renderFooter = () => (
        <div className="dialog-footer">
            <Button
                label="Cancel"
                icon="pi pi-times"
                className="btn-secondary"
                onClick={handleClose}
                outlined
            />
            <Button
                label="Continue"
                icon="pi pi-check"
                className="btn-primary"
                onClick={() => proceedVerify(true, state.otp)}
            />
        </div>
    );

    return (
        <div className="auth-container login-split">

            {/* ═══ LEFT — Navy brand-story panel ═══ */}
            <div className="ls-brandpanel">
                {/* decorative arcs */}
                <div className="ls-arc ls-arc1" />
                <div className="ls-arc ls-arc2" />
                <div className="ls-arc ls-arc3" />

                {/* brand lockup */}
                <div className="ls-bp-logo">
                    <div className="ls-bp-badge"><img src={logo} alt="Dunes" /></div>
                    {/* <div>
                        <div className="ls-bp-wm">Dun<span>es</span></div>
                        <div className="ls-bp-cat">Smart Campus Operating System</div>
                    </div> */}
                </div>

                <div className='ls-bp-hero' style={{ marginTop: 'auto', marginBottom: 'auto', width: '100%', maxWidth: '540px' }}>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.2rem', marginTop: '2rem' }}>
                        <div style={{ height: '1.5px', width: '32px', background: '#E9C96A' }}></div>
                        <span style={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: '2px', color: '#B3C4DE', textTransform: 'uppercase' }}>STUDENT PORTAL</span>
                    </div>

                    <h1 style={{ fontSize: '3.2rem', lineHeight: '1.15', letterSpacing: '-0.02em', fontWeight: 700, margin: '0' }}>
                        Learn Today.<br /><span style={{ color: '#E9C96A' }}>Lead Tomorrow.</span>
                    </h1>

                    <p style={{ marginTop: '1.8rem', lineHeight: '1.7', fontSize: '1.05rem', color: '#DCE4F5', fontWeight: 400, paddingRight: '2rem' }}>
                        Your central hub for learning resources, live classes, academic support and everything you need to keep your college journey moving forward.
                    </p>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '2.5rem', marginBottom: '2.5rem' }}>
                        <div style={{ height: '1.5px', width: '32px', background: '#E9C96A' }}></div>
                        <span style={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: '2px', color: '#B3C4DE', textTransform: 'uppercase' }}>EDUCATION EMPOWERS TOMORROW</span>
                    </div>

                    <div style={{ display: 'flex', gap: '2.5rem', marginTop: '1rem' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                            <i className="pi pi-book" style={{ fontSize: '1.8rem', color: '#fff' }}></i>
                            <div style={{ lineHeight: '1.3' }}>
                                <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#fff' }}>Learn</div>
                                <div style={{ fontSize: '0.75rem', color: '#B3C4DE' }}>Without Limits</div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                            <i className="pi pi-users" style={{ fontSize: '1.8rem', color: '#fff' }}></i>
                            <div style={{ lineHeight: '1.3' }}>
                                <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#fff' }}>Connect</div>
                                <div style={{ fontSize: '0.75rem', color: '#B3C4DE' }}>With Your Campus</div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                            <i className="pi pi-chart-bar" style={{ fontSize: '1.8rem', color: '#fff' }}></i>
                            <div style={{ lineHeight: '1.3' }}>
                                <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#fff' }}>Grow</div>
                                <div style={{ fontSize: '0.75rem', color: '#B3C4DE' }}>For a Brighter Future</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ═══ RIGHT — White OTP panel ═══ */}
            <div className="ls-formpanel">
                <div className="ls-formbox">
                    <h2 className="ls-title">
                        {step === "send" ? "Verify your identity" : "Enter verification code"}
                    </h2>
                    <p className="ls-subtitle">
                        {step === "send"
                            ? "We'll send a secure code to your mobile number"
                            : `Enter the 6-digit code sent to +91-****-***-${state.phone.slice(-3)}`
                        }
                    </p>

                    {step === "verify" && (
                        <div className="ls-steps">
                            <div className="ls-step ls-step--done"><i className="pi pi-check" /><span>Phone Verified</span></div>
                            <div className="ls-step ls-step--active"><i className="pi pi-key" /><span>Enter OTP</span></div>
                        </div>
                    )}

                    <form className="ls-form" onSubmit={(e) => { step === "send" ? handleSendOTP(e) : (step === "verify" ? handleVerifyOTP(e) : e.preventDefault()); }}>
                        {step === "send" && (
                            <>
                                <div className="ls-field-group">
                                    <label htmlFor="phone">Phone Number</label>
                                    <div className={`ls-field ${state.phoneErr ? 'ls-field--err' : ''}`}>
                                        <i className="pi pi-mobile" />
                                        <InputText
                                            id="phone"
                                            placeholder="Enter 10-digit phone number"
                                            type="text"
                                            maxLength={10}
                                            value={state.phone}
                                            onChange={(e) => handleCheck(e, "phone")}
                                        />
                                    </div>
                                    {state.phoneErr && <span className="ls-err">{state.phoneHelperText}</span>}
                                </div>

                                <Button type="submit" className="ls-submit" label="Send OTP" icon="pi pi-arrow-right" iconPos="right" />

                                <div className="ls-divider"><span>or</span></div>

                                <Button
                                    type="button"
                                    className="ls-otp-btn"
                                    label="Back to Password Login"
                                    icon="pi pi-key"
                                    outlined
                                    onClick={() => navigate("/login")}
                                />
                            </>
                        )}

                        {step === "verify" && (
                            <>
                                <div className="ls-otp-info">
                                    <i className="pi pi-check-circle" />
                                    <span>OTP sent to {state.phone}</span>
                                    <Button
                                        type="button"
                                        label="Edit"
                                        icon="pi pi-pencil"
                                        className="p-button-text p-button-sm"
                                        style={{ marginLeft: '0.5rem' }}
                                        onClick={() => {
                                            setStep("send");
                                            setState((s) => ({ ...s, otp: "", otpErr: false, otpHelperText: "" }));
                                        }}
                                    />
                                </div>

                                <div className="ls-field-group">
                                    <label htmlFor="otp">Verification Code</label>
                                    <div className={`ls-field ${state.otpErr ? 'ls-field--err' : ''}`}>
                                        <i className="pi pi-lock" />
                                        <InputText
                                            id="otp"
                                            placeholder="Enter 4-6 digit OTP"
                                            type="text"
                                            className="ls-otp-input"
                                            maxLength={6}
                                            value={state.otp}
                                            onChange={(e) => handleCheck(e, "otp")}
                                        />
                                    </div>
                                    {state.otpErr && <span className="ls-err">{state.otpHelperText}</span>}
                                </div>

                                <Button type="submit" className="ls-submit" label="Verify & Login" icon="pi pi-check" iconPos="right" />

                                <Button
                                    type="button"
                                    className="ls-otp-btn"
                                    label="Resend OTP"
                                    icon="pi pi-refresh"
                                    outlined
                                    onClick={handleSendOTP}
                                    style={{ marginTop: '.6rem' }}
                                />
                            </>
                        )}
                    </form>

                    <div style={{ display: captcha.visible ? 'block' : 'none', marginTop: '1rem' }}>
                        <div ref={captcha.containerRef} style={{ display: 'flex', justifyContent: 'center' }}></div>
                    </div>

                    <p className='ls-footer' style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <span>&copy; {new Date().getFullYear()} Dunes · Powered By&nbsp;</span>
                        <a href="https://portlinks.in" target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <img src={portlinkLogo} alt="PortLinks" style={{ height: '30px' }} />
                            PortLinks India
                        </a>
                    </p>
                </div>
            </div>

            <Dialog
                visible={state.openSchoolSelect}
                onHide={() => setState((s) => ({ ...s, openSchoolSelect: false }))}
                header={
                    <div className="dialog-header">
                        <i className="pi pi-building" />
                        <span className="dialog-title">Select your school</span>
                    </div>
                }
                draggable={false}
                dismissableMask
                position="center"
                className="confirmation-dialog"
                style={{ width: '440px' }}
            >
                <div className="ls-school-list">
                    {accounts.map((acc) => (
                        <button
                            type="button"
                            key={acc.userId}
                            className="ls-school-item"
                            onClick={() => handleSelectSchool(acc)}
                        >
                            <span className="ls-school-ic"><i className="pi pi-building" /></span>
                            <span className="ls-school-meta">
                                <span className="ls-school-name">{acc.schoolName || 'School'}</span>
                                {acc.board ? <span className="ls-school-sub">{acc.board}</span> : null}
                            </span>
                            <i className="pi pi-angle-right" />
                        </button>
                    ))}
                </div>
            </Dialog>

            {/* Max-session picker: choose a device to log out to free a slot */}
            <Dialog
                visible={state.openSessionLimit}
                onHide={() => { setState((s) => ({ ...s, openSessionLimit: false })); setPendingVerify(null); }}
                header={
                    <div className="dialog-header">
                        <i className="pi pi-desktop" />
                        <span className="dialog-title">Logged in on 3 devices</span>
                    </div>
                }
                draggable={false}
                dismissableMask
                position="center"
                className="confirmation-dialog"
                style={{ width: '460px' }}
            >
                <p style={{ margin: '0 0 12px', fontSize: '14px', color: '#6B7794' }}>
                    You can be signed in on up to 3 devices. Choose one to log out and continue.
                </p>
                <div className="ls-school-list">
                    {sessionList.map((s) => (
                        <button
                            type="button"
                            key={s._id}
                            className="ls-school-item"
                            onClick={() => handleRemoveSession(s._id)}
                        >
                            <span className="ls-school-ic"><i className="pi pi-desktop" /></span>
                            <span className="ls-school-meta">
                                <span className="ls-school-name">{s.device || 'Unknown device'}</span>
                                <span className="ls-school-sub">{formatLastActive(s.lastActive)}</span>
                            </span>
                            <i className="pi pi-sign-out" />
                        </button>
                    ))}
                </div>
            </Dialog>

            <Dialog
                visible={state.openLoginConfirm}
                onHide={handleClose}
                header={
                    <div className="dialog-header">
                        <i className="pi pi-exclamation-triangle warning-icon"></i>
                        <span className="dialog-title">Session Conflict</span>
                    </div>
                }
                footer={renderFooter()}
                draggable={false}
                dismissableMask
                position="center"
                className="confirmation-dialog"
                style={{ width: '440px' }}
            >
                <div className="dialog-content">
                    <p className="dialog-message">
                        You are already logged in on another device. Would you like to end that session and continue here?
                    </p>
                    <div className="session-info">
                        <i className="pi pi-info-circle"></i>
                        <span>This will log you out from all other active sessions</span>
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default OTPLogin;
